/**
 * Product Add to Cart
 * Feature: 019-product-page-carousel - US4
 *
 * Provides AJAX add-to-cart with:
 * - Form validation (all options selected)
 * - Loading states (disable button, show spinner)
 * - Error handling (422/429/500 responses, network failures)
 * - Success handling (screen reader announcement, mini basket integration)
 * - Prevent double submission
 */

class AddToCart {
  constructor(form) {
    if (!form) return;

    this.form = form;
    this.submitButton = form.querySelector('[data-add-to-cart-btn]');
    this.btnText = this.submitButton?.querySelector('[data-btn-text]');
    this.btnLoading = this.submitButton?.querySelector('[data-btn-loading]');
    this.errorContainer = form.querySelector('[data-error-message]');
    this.errorText = this.errorContainer?.querySelector('[data-error-text]');
    this.cartAnnouncement = document.querySelector('[data-cart-announcement]');
    this.variantIdInput = form.querySelector('[data-variant-id]');
    this.quantityInput = form.querySelector('input[name="quantity"]');

    this.isSubmitting = false;

    this.init();
  }

  /**
   * Initialize add-to-cart (T036)
   */
  init() {
    if (!this.form || !this.submitButton) return;

    // Prevent default form submission and handle via AJAX
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.handleSubmit();
    });
  }

  /**
   * Handle form submission (T037)
   * Gather form data, validate, call Shopify Cart API
   */
  async handleSubmit() {
    // Prevent double submission
    if (this.isSubmitting) return;

    // Validate variant selection (T041)
    if (!this.validateVariantSelection()) {
      return;
    }

    // Validate quantity
    if (!this.validateQuantity()) {
      return;
    }

    // Set loading state (T038)
    this.setLoadingState(true);

    // Gather form data
    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity'), 10) || 1;

    // Prepare request body for Shopify Cart API
    const requestBody = {
      items: [{
        id: variantId,
        quantity: quantity
      }]
    };

    try {
      // Call Shopify Cart API (T037)
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      // Check response status
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Unable to add to cart');
      }

      const data = await response.json();

      // Handle success (T039)
      this.handleSuccess(data, quantity);

    } catch (error) {
      // Handle error (T040)
      this.handleError(error);
    } finally {
      // Reset loading state
      this.setLoadingState(false);
    }
  }

  /**
   * Validate variant selection (T041)
   * Check that all product options have been selected
   */
  validateVariantSelection() {
    // Check if variant ID is set
    if (!this.variantIdInput || !this.variantIdInput.value) {
      this.showError(window.theme?.translations?.selectOptions || 'Please select all options');
      return false;
    }

    // Check all option dropdowns have values
    const variantSelectors = document.querySelectorAll('[data-option-select]');
    for (const selector of variantSelectors) {
      if (!selector.value) {
        this.showError(window.theme?.translations?.selectOptions || 'Please select all options');
        return false;
      }
    }

    return true;
  }

  /**
   * Validate quantity
   */
  validateQuantity() {
    if (!this.quantityInput) return true;

    const quantity = parseInt(this.quantityInput.value, 10);
    const min = parseInt(this.quantityInput.min, 10) || 1;
    const max = parseInt(this.quantityInput.max, 10) || 99;

    if (isNaN(quantity) || quantity < min || quantity > max) {
      this.showError(
        window.theme?.translations?.quantityError ||
        `Please enter a quantity between ${min} and ${max}`
      );
      return false;
    }

    return true;
  }

  /**
   * Set loading state (T038)
   * Disable button, show spinner, prevent double submission
   */
  setLoadingState(isLoading) {
    this.isSubmitting = isLoading;

    if (!this.submitButton) return;

    if (isLoading) {
      // Show loading state
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');

      if (this.btnText) this.btnText.hidden = true;
      if (this.btnLoading) this.btnLoading.hidden = false;

      // Hide any previous errors
      this.hideError();
    } else {
      // Reset to normal state
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');

      if (this.btnText) this.btnText.hidden = false;
      if (this.btnLoading) this.btnLoading.hidden = true;
    }
  }

  /**
   * Handle successful add-to-cart (T039)
   * Announce to screen reader, trigger mini basket, reset form
   */
  handleSuccess(data, quantity) {
    // Get product title from data
    const productTitle = data.items?.[0]?.product_title || 'Product';

    // Announce to screen readers
    this.announceToScreenReader(
      `${quantity} × ${productTitle} added to cart`
    );

    // Trigger mini basket to open (if enabled in theme settings and available)
    const openDrawerOnAdd = window.theme?.settings?.cartOpenDrawerOnAdd !== false;
    if (openDrawerOnAdd && window.miniBasket && typeof window.miniBasket.open === 'function') {
      // Give screen reader announcement a moment to be read
      setTimeout(() => {
        window.miniBasket.open();
      }, 100);
    }

    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('cart:item-added', {
      detail: {
        items: data.items,
        quantity: quantity
      }
    }));

    // Dispatch cart:updated event to trigger header badge update
    document.dispatchEvent(new CustomEvent('cart:updated'));

    // Reset quantity to 1 (optional)
    if (this.quantityInput) {
      this.quantityInput.value = 1;
    }
  }

  /**
   * Handle error (T040)
   * Display user-friendly error, parse response codes, retry logic
   */
  handleError(error) {
    console.error('Add to cart error:', error);

    let errorMessage = error.message || 'Unable to add to cart. Please try again.';

    // Parse specific error codes
    if (error.message) {
      // 422: Variant sold out or unavailable
      if (error.message.includes('sold out') || error.message.includes('unavailable')) {
        errorMessage = window.theme?.translations?.variantSoldOut ||
          'This variant is sold out';
      }
      // 429: Rate limited
      else if (error.message.includes('429') || error.message.includes('rate')) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      }
      // Network failure
      else if (error.message.includes('fetch') || error.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      // Generic cart error
      else if (error.message.includes('cart')) {
        errorMessage = window.theme?.translations?.cartError ||
          'Unable to add to cart. Please try again.';
      }
    }

    // Show error message
    this.showError(errorMessage);

    // Announce error to screen readers
    this.announceToScreenReader(`Error: ${errorMessage}`);
  }

  /**
   * Show error message
   */
  showError(message) {
    if (!this.errorContainer || !this.errorText) return;

    this.errorText.textContent = message;
    this.errorContainer.hidden = false;

    // Auto-hide error after 5 seconds
    setTimeout(() => {
      this.hideError();
    }, 5000);
  }

  /**
   * Hide error message
   */
  hideError() {
    if (this.errorContainer) {
      this.errorContainer.hidden = true;
    }
  }

  /**
   * Announce to screen readers (T039)
   */
  announceToScreenReader(message) {
    if (!this.cartAnnouncement) return;

    // Clear previous announcement
    this.cartAnnouncement.textContent = '';

    // Set new announcement (force screen reader to notice the change)
    setTimeout(() => {
      this.cartAnnouncement.textContent = message;
    }, 100);

    // Clear after announcement has been read
    setTimeout(() => {
      this.cartAnnouncement.textContent = '';
    }, 3000);
  }
}

/**
 * Initialize add-to-cart on page load
 */
document.addEventListener('DOMContentLoaded', function() {
  const productForm = document.querySelector('[data-product-form]');

  if (productForm) {
    // Initialize add-to-cart
    const addToCart = new AddToCart(productForm);

    // Make accessible globally for debugging/testing
    window.productAddToCart = addToCart;
  }
});

/**
 * Export for module systems (if needed)
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AddToCart;
}
